import { Component, OnInit, computed, inject, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Sidebar } from '../../shared/components/sidebar/sidebar';
import { Navbar } from '../../shared/components/navbar/navbar';
import {
  LineReview, LineReviewStats, VersionControl, VersionControlMode, VersionControlSettings
} from '../../core/services/version-control';

const CHANGE_TYPE_LABELS: Record<string, string> = {
  removed: 'Removed in the new version',
  wording: 'Wording only',
  numbers_or_dates: 'Number or date changed',
  obligation: 'Obligation changed',
  scope_added: 'Something added',
  scope_removed: 'Something removed',
  other: 'Other change',
};

/** One new line's open pairs within one old document: the reviewer picks at most one. */
export interface ReviewGroup {
  key: string;
  newText: string;
  newDocumentName: string;
  oldDocumentName: string;
  options: LineReview[];
}

const NONE = 'none';
const AUTO = 'auto';

/** Decided without a reviewer: by the date rule ('auto') or by the AI review ('ai'). */
export function isAutomatic(review: Pick<LineReview, 'decidedBy'>): boolean {
  return review.decidedBy === 'auto' || review.decidedBy === 'ai';
}

/** The status pill of a decided pair. */
export function pillText(review: Pick<LineReview, 'decidedBy' | 'autoRejectedByReviewId' | 'status'>): string {
  if (review.autoRejectedByReviewId || !isAutomatic(review)) return review.status === 'historical' ? 'history' : review.status;
  return review.decidedBy === 'ai' ? 'Auto: AI' : 'Auto: newer file';
}

/** The footer line of a decided pair (not a sibling closed by its group's decision). */
export function decidedText(review: Pick<LineReview, 'decidedBy' | 'autoRejectedByReviewId' | 'status'>): string {
  if (review.status === 'historical') {
    const prefix = !review.autoRejectedByReviewId && review.decidedBy === 'ai' ? 'Auto: AI: ' : '';
    return `${prefix}Both true, at different times: the old sentence is kept as history`;
  }
  if (!review.autoRejectedByReviewId && review.decidedBy === 'ai') {
    return review.status === 'approved'
      ? 'Auto: AI: the new sentence replaced this one'
      : 'Auto: AI: both sentences were kept';
  }
  if (!review.autoRejectedByReviewId && review.decidedBy === 'auto') {
    return review.status === 'approved'
      ? 'Auto: newer file: the newer file replaced this sentence'
      : 'Auto: newer file: the current sentence was kept (the incoming file is older)';
  }
  return review.status === 'approved' ? 'Approved' : 'Rejected';
}

/** The hint under the Manual / Auto switch. */
export function modeHint(mode: VersionControlMode | null): string {
  if (mode === 'manual') return 'A person approves every change.';
  if (mode === 'auto') {
    return 'Changes are approved automatically: clear cases by the newer file\'s date, the rest by an AI review. '
      + 'Anything the AI is unsure about still waits for a person.';
  }
  return 'Not set yet: works as before, as configured on the server.';
}

const norm = (w: string) => w.toLowerCase().replace(/[^\p{L}\p{N}]/gu, '');

/** Edit distance, for spotting the same word spelled slightly differently. */
function editDistance(a: string, b: string): number {
  let prev = Array.from({ length: b.length + 1 }, (_, j) => j);
  for (let i = 1; i <= a.length; i++) {
    const row = [i];
    for (let j = 1; j <= b.length; j++) {
      row[j] = Math.min(prev[j] + 1, row[j - 1] + 1, prev[j - 1] + (a[i - 1] === b[j - 1] ? 0 : 1));
    }
    prev = row;
  }
  return prev[b.length];
}

/** The same word, allowing small spelling differences in longer words ("BigEntities" ~ "BigEntitites"). */
function sameWord(a: string, b: string): boolean {
  if (!a || !b) return false;
  if (a === b) return true;
  if (Math.min(a.length, b.length) < 5) return false;
  return editDistance(a, b) <= Math.max(1, Math.floor(Math.max(a.length, b.length) / 5));
}

/**
 * Where the new sentence's change belongs in the old sentence: the old sentence with the stretch the
 * new one restates replaced by the new wording, e.g. "…because Faizan is CEO of BigEntitites and has
 * been…" + "Ammar is CEO BigEntities." gives "…because Ammar is CEO BigEntities and has been…".
 * Found by local word alignment (matching words, tolerant of small spelling differences); the stretch
 * is widened by the new sentence's unmatched words at either end ("Faizan" -> "Ammar"). Returns the new
 * sentence when it restates the whole old one, and null when fewer than two words line up.
 */
export function placeChange(oldText: string, newText: string): string | null {
  const oldWords = oldText.split(/\s+/).filter(Boolean);
  const newWords = newText.split(/\s+/).filter(Boolean);
  const o = oldWords.map(norm);
  const n = newWords.map(norm);
  const MATCH = 2, MISMATCH = -1, GAP = -1;

  // Smith-Waterman local alignment over words.
  const h: number[][] = Array.from({ length: o.length + 1 }, () => new Array(n.length + 1).fill(0));
  let best = 0, bi = 0, bj = 0;
  for (let i = 1; i <= o.length; i++) {
    for (let j = 1; j <= n.length; j++) {
      const diag = h[i - 1][j - 1] + (sameWord(o[i - 1], n[j - 1]) ? MATCH : MISMATCH);
      h[i][j] = Math.max(0, diag, h[i - 1][j] + GAP, h[i][j - 1] + GAP);
      if (h[i][j] > best) { best = h[i][j]; bi = i; bj = j; }
    }
  }
  if (best < 2 * MATCH) return null;

  // Trace back to where the aligned stretch starts.
  let i = bi, j = bj;
  while (i > 0 && j > 0 && h[i][j] > 0) {
    if (h[i][j] === h[i - 1][j - 1] + (sameWord(o[i - 1], n[j - 1]) ? MATCH : MISMATCH)) { i--; j--; }
    else if (h[i][j] === h[i - 1][j] + GAP) i--;
    else j--;
  }
  const start = Math.max(0, i - j);                                   // widen left by unmatched leading new words
  const end = Math.min(oldWords.length, bi + (newWords.length - bj)); // and right by unmatched trailing ones
  if (start === 0 && end === oldWords.length) return newText;

  const replacement = [...newWords];
  if (end < oldWords.length) {
    // The old sentence goes on: drop the new sentence's full stop, keep the old word's comma etc.
    const trailing = oldWords[end - 1].match(/[,;:]+$/)?.[0] ?? '';
    replacement[replacement.length - 1] = replacement[replacement.length - 1].replace(/[.!?]+$/, '') + trailing;
  }
  return [...oldWords.slice(0, start), ...replacement, ...oldWords.slice(end)].join(' ');
}

/** Starting text for "Edit replacement": the placed change, or the old sentence to edit by hand. */
export function suggestEdit(oldText: string, newText: string): string {
  return placeChange(oldText, newText) ?? oldText;
}

export interface DiffPart { op: 'equal' | 'delete' | 'insert' | 'gap'; text: string; }

/** Word diff old -> new, with long unchanged stretches folded to "…" so the change stands out. */
export function compactDiff(oldText: string, newText: string, context = 6): DiffPart[] {
  const a = oldText.split(/\s+/).filter(Boolean);
  const b = newText.split(/\s+/).filter(Boolean);
  const lcs: number[][] = Array.from({ length: a.length + 1 }, () => new Array(b.length + 1).fill(0));
  for (let i = a.length - 1; i >= 0; i--)
    for (let j = b.length - 1; j >= 0; j--)
      lcs[i][j] = a[i] === b[j] ? lcs[i + 1][j + 1] + 1 : Math.max(lcs[i + 1][j], lcs[i][j + 1]);
  const ops: DiffPart[] = [];
  const push = (op: DiffPart['op'], word: string) => {
    const last = ops[ops.length - 1];
    last && last.op === op ? (last.text += ' ' + word) : ops.push({ op, text: word });
  };
  let i = 0, j = 0;
  while (i < a.length || j < b.length) {
    if (i < a.length && j < b.length && a[i] === b[j]) { push('equal', a[i]); i++; j++; }
    // Deletions before insertions, so a swap reads "~~old~~ new".
    else if (i < a.length && (j === b.length || lcs[i + 1][j] >= lcs[i][j + 1])) { push('delete', a[i]); i++; }
    else { push('insert', b[j]); j++; }
  }
  return ops.flatMap((part, k) => {
    if (part.op !== 'equal') return [part];
    const words = part.text.split(' ');
    const keepHead = k === 0 ? 0 : context, keepTail = k === ops.length - 1 ? 0 : context;
    if (words.length <= keepHead + keepTail + 1) return [part];
    return [
      ...(keepHead ? [{ op: 'equal' as const, text: words.slice(0, keepHead).join(' ') }] : []),
      { op: 'gap' as const, text: '…' },
      ...(keepTail ? [{ op: 'equal' as const, text: words.slice(-keepTail).join(' ') }] : []),
    ];
  });
}

@Component({
  selector: 'app-version-review',
  standalone: true,
  imports: [CommonModule, FormsModule, Sidebar, Navbar],
  templateUrl: './version-review.html',
  styleUrl: './version-review.css'
})
export class VersionReviewPage implements OnInit {
  private versionControl = inject(VersionControl);

  reviews = signal<LineReview[]>([]);
  stats = signal<LineReviewStats | null>(null);
  settings = signal<VersionControlSettings | null>(null);
  savingMode = signal(false);
  total = signal(0);
  page = signal(1);
  pageSize = 50;
  loading = signal(false);
  error = signal<string | null>(null);
  busyIds = signal<Set<string>>(new Set());
  statusFilter = signal<string>('pending');
  /** Group key -> chosen review id, or 'none'. */
  choice = signal<Record<string, string>>({});
  /** Group key -> edited replacement text, while "Edit replacement" is open. */
  editing = signal<Record<string, string>>({});
  /** Group keys where the reviewer chose to replace the whole old sentence instead of the proposal. */
  wholeSentence = signal<Record<string, boolean>>({});
  readonly none = NONE;

  /** Pending pairs grouped by new line + old document, in list order. */
  groups = computed<ReviewGroup[]>(() => {
    const byKey = new Map<string, ReviewGroup>();
    for (const r of this.reviews()) {
      const key = `${r.newLineId}|${r.oldDocumentId}`;
      let group = byKey.get(key);
      if (!group) {
        group = {
          key,
          newText: r.newText,
          newDocumentName: r.newDocumentName || r.documentId,
          oldDocumentName: r.oldDocumentName || r.oldDocumentId,
          options: []
        };
        byKey.set(key, group);
      }
      group.options.push(r);
    }
    const groups = [...byKey.values()];
    groups.forEach(g => g.options.sort((a, b) => b.similarityPct - a.similarityPct));
    return groups;
  });

  ngOnInit(): void {
    this.versionControl.settings().subscribe({ next: s => this.settings.set(s) });
    this.refresh();
  }

  refresh(): void {
    this.loading.set(true);
    this.error.set(null);
    this.versionControl.stats().subscribe({ next: s => this.stats.set(s) });
    // "Auto-accepted" is every status decided from document dates, not a status of its own.
    const auto = this.statusFilter() === AUTO;
    this.versionControl
      .listReviews({
        status: auto ? '' : this.statusFilter(),
        decidedBy: auto ? AUTO : undefined,
        page: this.page(),
        pageSize: this.pageSize
      })
      .subscribe({
        next: res => {
          this.reviews.set(res.items);
          this.total.set(res.total);
          this.loading.set(false);
        },
        error: () => {
          this.error.set('Could not load updates to review. Refresh to try again.');
          this.loading.set(false);
        }
      });
  }

  onFilterChange(): void {
    this.page.set(1);
    this.refresh();
  }

  changeTypeLabel(type: string | null): string {
    return type ? CHANGE_TYPE_LABELS[type] ?? type : 'Change';
  }

  isBusy(id: string): boolean {
    return this.busyIds().has(id);
  }

  choose(groupKey: string, value: string): void {
    this.choice.set({ ...this.choice(), [groupKey]: value });
    this.cancelEdit(groupKey); // a suggestion belongs to one option
    this.setWhole(groupKey, false);
  }

  /** The old sentence with the change put in place, when that differs from just taking the new sentence. */
  proposal(review: LineReview): string | null {
    const placed = placeChange(review.oldText, review.newText);
    return placed && placed !== review.newText ? placed : null;
  }

  /** What the old sentence becomes if the chosen option is confirmed as it stands. */
  outcome(group: ReviewGroup, review: LineReview): string {
    const proposed = this.proposal(review);
    return proposed && !this.isWhole(group) ? proposed : review.newText;
  }

  diffFor(oldText: string, newText: string): DiffPart[] {
    return compactDiff(oldText, newText);
  }

  isWhole(group: ReviewGroup): boolean {
    return !!this.wholeSentence()[group.key];
  }

  setWhole(groupKey: string, whole: boolean): void {
    this.wholeSentence.set({ ...this.wholeSentence(), [groupKey]: whole });
  }

  confirmLabel(group: ReviewGroup): string {
    if (this.isBusy(group.key)) return 'Saving…';
    if (this.isEditing(group)) return 'Confirm with edit';
    const review = this.chosenReview(group);
    return review && this.proposal(review) && !this.isWhole(group) ? 'Apply proposed update' : 'Confirm';
  }

  /** A "removed?" card: an older document's sentence its new version no longer has. */
  isRemoval(group: ReviewGroup): boolean {
    return group.options[0]?.changeType === 'removed';
  }

  sameBatch(group: ReviewGroup): boolean {
    return group.options.some(o => o.sameBatch);
  }

  chosenReview(group: ReviewGroup): LineReview | undefined {
    return group.options.find(o => o.id === this.chosen(group));
  }

  isEditing(group: ReviewGroup): boolean {
    return group.key in this.editing();
  }

  startEdit(group: ReviewGroup, review: LineReview): void {
    const start = this.isWhole(group) ? review.newText : this.proposal(review) ?? suggestEdit(review.oldText, review.newText);
    this.editing.set({ ...this.editing(), [group.key]: start });
  }

  setEdit(groupKey: string, text: string): void {
    this.editing.set({ ...this.editing(), [groupKey]: text });
  }

  cancelEdit(groupKey: string): void {
    const { [groupKey]: _, ...rest } = this.editing();
    this.editing.set(rest);
  }

  canConfirm(group: ReviewGroup): boolean {
    if (!this.chosen(group) || this.isBusy(group.key)) return false;
    return !this.isEditing(group) || !!this.editing()[group.key]?.trim();
  }

  chosen(group: ReviewGroup): string | undefined {
    return this.choice()[group.key];
  }

  /** Approve the chosen pair (the backend closes the rest of the group), or reject them all. */
  confirm(group: ReviewGroup): void {
    const value = this.chosen(group);
    if (!value) return;
    // Edited text, else the proposed placement (unless "replace the whole sentence" was chosen), else a plain replace.
    const review = this.chosenReview(group);
    const proposed = review && !this.isWhole(group) ? this.proposal(review) : null;
    const edited = this.isEditing(group) ? this.editing()[group.key].trim() : proposed ?? undefined;
    const call = value === NONE
      ? this.versionControl.rejectGroup(group.options[0].id)
      : this.versionControl.approve(value, edited);
    this.run(group.key, call, value === NONE ? 'keep these sentences' : 'approve this update');
  }

  /** "Both true, at different times" is offered for a chosen stored sentence, not for removals or edits. */
  canKeepAsHistory(group: ReviewGroup): boolean {
    const value = this.chosen(group);
    return !!value && value !== NONE && !this.isRemoval(group) && !this.isEditing(group) && !this.isBusy(group.key);
  }

  /** The new sentence takes over; the chosen stored one is kept as history on the timeline. */
  keepAsHistory(group: ReviewGroup): void {
    const value = this.chosen(group);
    if (!value || value === NONE) return;
    this.run(group.key, this.versionControl.markHistorical(value), 'keep the old sentence as history');
  }

  /** Why a pair was closed by its group's decision: the reviewer chose another sentence, or none. */
  closedLabel(review: LineReview): string {
    if (isAutomatic(review)) return 'Closed by an automatic decision on the same sentence';
    const decider = this.reviews().find(r => r.id === review.autoRejectedByReviewId);
    return decider?.status === 'rejected'
      ? 'Rejected: none of these was chosen'
      : 'Closed: another sentence was chosen';
  }

  /** Decided without a reviewer (the decision itself, not a closed sibling). */
  isAutoAccepted(review: LineReview): boolean {
    return isAutomatic(review) && !review.autoRejectedByReviewId;
  }

  decidedLabel(review: LineReview): string {
    return decidedText(review);
  }

  pillLabel(review: LineReview): string {
    return pillText(review);
  }

  modeHint(): string {
    return modeHint(this.settings()?.mode ?? null);
  }

  /** Manual / Auto switch. */
  setMode(mode: VersionControlMode): void {
    const current = this.settings();
    if (!current?.canChange || current.mode === mode || this.savingMode()) return;
    this.savingMode.set(true);
    this.error.set(null);
    this.versionControl.setMode(mode).subscribe({
      next: s => { this.settings.set(s); this.savingMode.set(false); },
      error: () => {
        this.savingMode.set(false);
        this.error.set('Could not change how reviews are approved. Please try again.');
      }
    });
  }

  /** Which side's document is newer, when both dates are known. */
  newerSide(review: LineReview): 'new' | 'old' | null {
    if (!review.newDocumentDate || !review.oldDocumentDate) return null;
    const diff = Date.parse(review.newDocumentDate) - Date.parse(review.oldDocumentDate);
    return diff > 0 ? 'new' : diff < 0 ? 'old' : null;
  }

  dateSourceHint(source: string | null | undefined): string {
    return source === 'document_text' ? 'Stated in the document itself (e.g. "Date: ...")'
      : source === 'file_metadata' ? "From the file's own properties (last saved)"
      : source === 'source_modified' ? 'Last modified in the system it came from'
      : '';
  }

  undo(review: LineReview): void {
    this.run(review.id, this.versionControl.undo(review.id), 'undo this decision');
  }

  private run(busyKey: string, call: ReturnType<VersionControl['approve']>, what: string): void {
    this.setBusy(busyKey, true);
    this.error.set(null);
    call.subscribe({
      next: () => {
        this.setBusy(busyKey, false);
        const { [busyKey]: _, ...rest } = this.choice();
        this.choice.set(rest);
        this.cancelEdit(busyKey);
        this.refresh();
      },
      error: err => {
        this.setBusy(busyKey, false);
        // A 409 explains itself (already chosen, undo blocked); anything else is a retry.
        this.error.set(err?.status === 409 && err?.error?.detail ? err.error.detail : `Could not ${what}. Try again.`);
      }
    });
  }

  get totalPages(): number {
    return Math.max(1, Math.ceil(this.total() / this.pageSize));
  }

  goToPage(page: number): void {
    if (page < 1 || page > this.totalPages) return;
    this.page.set(page);
    this.refresh();
  }

  private setBusy(id: string, busy: boolean): void {
    const next = new Set(this.busyIds());
    busy ? next.add(id) : next.delete(id);
    this.busyIds.set(next);
  }
}
